import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

// ── Parser ────────────────────────────────────────────────────

function splitPlain(text) {
    return text
        .split(/\n{2,}/)
        .map(s => s.trim())
        .filter(s => s && !/^[-*_]{3,}$/.test(s))
        .map(s => ({
            type: /^#{1,6} /.test(s) ? 'heading' : 'paragraph',
            title: null,
            content: s,
        }));
}

function parseBlocks(text) {
    const blocks = [];
    const pattern = /^:::(\w+)(?:[^\S\n]+(.+?))?\n([\s\S]*?)^:::/gm;
    let offset = 0;
    let match;

    // Direct mapping of French and English aliases to standardized components
    const typeMap = {
        example: 'example',
        exemple: 'example',
        exemples: 'example',
        theorem: 'theorem',
        theoreme: 'theorem',
        proof: 'proof',
        demonstration: 'proof',
        demo: 'proof',
        remark: 'remark',
        remarque: 'remark',
        note: 'note',
        definition: 'definition',
        image_prompt: 'image_prompt',
        schema: 'image_prompt',
    };

    while ((match = pattern.exec(text)) !== null) {
        // Texte ordinaire avant ce bloc fencé
        const before = text.slice(offset, match.index);
        splitPlain(before).forEach(s => blocks.push(s));

        const rawType = match[1].toLowerCase();
        const type = typeMap[rawType] || 'paragraph';

        blocks.push({
            type: type,
            title: match[2]?.trim() || null,
            content: match[3].trim(),
        });
        offset = match.index + match[0].length;
    }

    splitPlain(text.slice(offset)).forEach(s => blocks.push(s));
    return blocks;
}

// ── Styles par type ───────────────────────────────────────────

export const BLOCK_CONFIG = {
    definition: {
        border: 'border-l-4 border-blue-500/80 dark:border-blue-400',
        bg: 'bg-blue-50/50 dark:bg-blue-950/20',
        label: 'Définition',
        color: 'text-blue-700 dark:text-blue-400',
    },
    theorem: {
        border: 'border-l-4 border-indigo-500/80 dark:border-indigo-400',
        bg: 'bg-indigo-50/50 dark:bg-indigo-950/20',
        label: 'Théorème',
        color: 'text-indigo-700 dark:text-indigo-400',
    },
    proof: {
        border: 'border-l-4 border-gray-400/80 dark:border-gray-500',
        bg: 'bg-gray-50/70 dark:bg-gray-900/30',
        label: 'Démonstration',
        color: 'text-gray-600 dark:text-gray-400',
    },
    example: {
        border: 'border-l-4 border-emerald-500/80 dark:border-emerald-400',
        bg: 'bg-emerald-50/50 dark:bg-emerald-950/20',
        label: 'Exemple',
        color: 'text-emerald-700 dark:text-emerald-400',
    },
    remark: {
        border: 'border-l-4 border-amber-500/80 dark:border-amber-400',
        bg: 'bg-amber-50/50 dark:bg-amber-950/20',
        label: 'Remarque',
        color: 'text-amber-700 dark:text-amber-400',
    },
    note: {
        border: 'border-l-4 border-cyan-500/80 dark:border-cyan-400',
        bg: 'bg-cyan-50/50 dark:bg-cyan-950/20',
        label: 'Note',
        color: 'text-cyan-700 dark:text-cyan-400',
    },
    image_prompt: {
        border: 'border-2 border-dashed border-orange-300 dark:border-orange-600',
        bg: 'bg-orange-50/50 dark:bg-orange-950/10',
        label: '📷 Schéma à générer',
        color: 'text-orange-700 dark:text-orange-400',
    },
};

// ── Spoilers en ligne de style Discord (||spoiler||) ──────────

function InlineSpoiler({ children }) {
    const [revealed, setRevealed] = useState(false);

    return (
        <span
            onClick={(e) => {
                e.stopPropagation();
                setRevealed(true);
            }}
            className={`inline rounded px-1.5 py-0.5 transition-all duration-200 cursor-pointer select-none font-semibold ${
                revealed
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-default'
                    : 'bg-gray-950 dark:bg-gray-700 text-transparent selection:bg-transparent select-none hover:bg-gray-900 dark:hover:bg-gray-600'
            }`}
            title={revealed ? '' : 'Cliquez pour dévoiler'}
        >
            {children}
        </span>
    );
}

function groupSpoilers(children) {
    if (!children) return children;
    const array = Array.isArray(children) ? children : [children];
    const newChildren = [];
    let insideSpoiler = false;
    let currentSpoilerChildren = [];

    for (let i = 0; i < array.length; i++) {
        const child = array[i];

        if (typeof child === 'string') {
            if (child.includes('||')) {
                const parts = child.split('||');
                for (let j = 0; j < parts.length; j++) {
                    const part = parts[j];
                    if (j === 0) {
                        if (insideSpoiler) {
                            if (part) currentSpoilerChildren.push(part);
                            newChildren.push(
                                <InlineSpoiler key={`spoiler-${i}-${j}`}>
                                    {currentSpoilerChildren}
                                </InlineSpoiler>
                            );
                            currentSpoilerChildren = [];
                            insideSpoiler = false;
                        } else {
                            if (part) newChildren.push(part);
                        }
                    } else {
                        // For subsequent parts: they alternate between inside and outside
                        if (insideSpoiler) {
                            if (part) currentSpoilerChildren.push(part);
                            newChildren.push(
                                <InlineSpoiler key={`spoiler-${i}-${j}`}>
                                    {currentSpoilerChildren}
                                </InlineSpoiler>
                            );
                            currentSpoilerChildren = [];
                            insideSpoiler = false;
                        } else {
                            // Start new spoiler
                            insideSpoiler = true;
                            currentSpoilerChildren = [];
                            if (part) currentSpoilerChildren.push(part);
                        }
                    }
                }
            } else {
                if (insideSpoiler) {
                    currentSpoilerChildren.push(child);
                } else {
                    newChildren.push(child);
                }
            }
        } else {
            // It's a React element or another object
            if (insideSpoiler) {
                currentSpoilerChildren.push(child);
            } else {
                newChildren.push(child);
            }
        }
    }

    // Flush any unclosed spoiler
    if (insideSpoiler && currentSpoilerChildren.length > 0) {
        newChildren.push(
            <InlineSpoiler key="spoiler-unclosed">
                {currentSpoilerChildren}
            </InlineSpoiler>
        );
    }

    return newChildren.length === 1 ? newChildren[0] : newChildren;
}

const MD_COMPONENTS = {
    p:      ({ children, ...props }) => <p {...props}>{groupSpoilers(children)}</p>,
    li:     ({ children, ...props }) => <li {...props}>{groupSpoilers(children)}</li>,
    td:     ({ children, ...props }) => <td {...props}>{groupSpoilers(children)}</td>,
    th:     ({ children, ...props }) => <th {...props}>{groupSpoilers(children)}</th>,
    h1:     ({ children, ...props }) => <h1 {...props}>{groupSpoilers(children)}</h1>,
    h2:     ({ children, ...props }) => <h2 {...props}>{groupSpoilers(children)}</h2>,
    h3:     ({ children, ...props }) => <h3 {...props}>{groupSpoilers(children)}</h3>,
    h4:     ({ children, ...props }) => <h4 {...props}>{groupSpoilers(children)}</h4>,
    h5:     ({ children, ...props }) => <h5 {...props}>{groupSpoilers(children)}</h5>,
    h6:     ({ children, ...props }) => <h6 {...props}>{groupSpoilers(children)}</h6>,
};

const MD = {
    remarkPlugins: [remarkGfm, remarkMath],
    rehypePlugins: [rehypeKatex],
    components: MD_COMPONENTS,
};

// ── Bloc de Base (Générique) ──────────────────────────────────

function BaseBlock({ type, title, children }) {
    const cfg = BLOCK_CONFIG[type] || BLOCK_CONFIG.note;
    return (
        <div className={`${cfg.border} ${cfg.bg} rounded-r-2xl px-5 py-4 my-3 shadow-sm dark:shadow-none transition-all hover:shadow-md dark:hover:bg-opacity-40`}>
            <p className={`text-xs font-extrabold uppercase tracking-wider mb-2.5 flex items-center gap-1.5 ${cfg.color}`}>
                <span>{title ? `${cfg.label} — ${title}` : cfg.label}</span>
            </p>
            <div className="prose prose-sm dark:prose-invert max-w-none [&_.katex-display]:overflow-x-auto [&_.katex-display]:py-1">
                {children}
            </div>
        </div>
    );
}

// ── Composants Spécifiques Dérivés ────────────────────────────

function DefinitionBlock({ title, content }) {
    return (
        <BaseBlock type="definition" title={title}>
            <ReactMarkdown {...MD}>{content}</ReactMarkdown>
        </BaseBlock>
    );
}

function TheoremBlock({ title, content }) {
    return (
        <BaseBlock type="theorem" title={title}>
            <ReactMarkdown {...MD}>{content}</ReactMarkdown>
        </BaseBlock>
    );
}

function ProofBlock({ title, content }) {
    return (
        <BaseBlock type="proof" title={title}>
            <ReactMarkdown {...MD}>{content}</ReactMarkdown>
        </BaseBlock>
    );
}

function ExamplePairItem({ question, solution }) {
    const [showSolution, setShowSolution] = useState(false);

    return (
        <div className="space-y-3">
            {question.trim() && (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown {...MD}>{question}</ReactMarkdown>
                </div>
            )}

            {solution && solution.trim() && (
                <div className="mt-2 pt-2 border-t border-emerald-100/40 dark:border-emerald-950/20">
                    {showSolution ? (
                        <div className="animate-fade-in space-y-2 pl-4 border-l-2 border-emerald-500/30">
                            <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                                💡 Explication / Solution
                            </p>
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                                <ReactMarkdown {...MD}>{solution}</ReactMarkdown>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowSolution(false)}
                                className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium underline mt-1 flex items-center gap-1 cursor-pointer"
                            >
                                🙈 Masquer la réponse
                            </button>
                        </div>
                    ) : (
                        <div className="pl-4">
                            <button
                                type="button"
                                onClick={() => setShowSolution(true)}
                                className="py-1.5 px-3 rounded-lg border border-emerald-200/60 dark:border-emerald-800/30 bg-emerald-50/30 dark:bg-emerald-950/10 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 font-semibold text-[11px] transition-all hover:scale-[1.01] flex items-center gap-1.5 cursor-pointer shadow-sm"
                            >
                                👁️ Afficher la réponse
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ExampleBlock({ title, content }) {
    // Split the content by lines that only consist of 3 or more dashes/asterisks/underscores
    const lines = content.split(/\r?\n/);
    const segments = [];
    let currentSegmentLines = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (/^[-*_]{3,}$/.test(line.trim())) {
            segments.push(currentSegmentLines.join('\n'));
            currentSegmentLines = [];
        } else {
            currentSegmentLines.push(line);
        }
    }
    segments.push(currentSegmentLines.join('\n'));

    // Pair up questions (even indexes) and solutions (odd indexes)
    const pairs = [];
    for (let i = 0; i < segments.length; i += 2) {
        pairs.push({
            question: segments[i],
            solution: segments[i + 1] || null
        });
    }

    return (
        <BaseBlock type="example" title={title}>
            <div className="space-y-6">
                {pairs.map((pair, index) => (
                    <ExamplePairItem 
                        key={index}
                        question={pair.question}
                        solution={pair.solution}
                    />
                ))}
            </div>
        </BaseBlock>
    );
}

function RemarkBlock({ title, content }) {
    return (
        <BaseBlock type="remark" title={title}>
            <ReactMarkdown {...MD}>{content}</ReactMarkdown>
        </BaseBlock>
    );
}

function NoteBlock({ title, content }) {
    return (
        <BaseBlock type="note" title={title}>
            <ReactMarkdown {...MD}>{content}</ReactMarkdown>
        </BaseBlock>
    );
}

function ImagePromptBlock({ title, content }) {
    return (
        <BaseBlock type="image_prompt" title={title}>
            <p className="text-sm text-orange-800 dark:text-orange-300 italic leading-relaxed">
                {content}
            </p>
        </BaseBlock>
    );
}

// ── Numérotation automatique des headings ─────────────────────

function addHeadingNumbers(blocks) {
    let h2 = 0, h3 = 0;
    return blocks.map(block => {
        if (block.type !== 'heading') return block;
        const m = block.content.match(/^(#{1,6}) /);
        const level = m ? m[1].length : 2;
        if (level <= 2) { h2++; h3 = 0; return { ...block, _prefix: `${h2}`, _level: 2 }; }
        h3++;
        return { ...block, _prefix: `${h2}.${h3}`, _level: 3 };
    });
}

// ── Composant Principal ───────────────────────────────────────

export default function CourseBlockRenderer({ content = '' }) {
    const blocks = useMemo(() => addHeadingNumbers(parseBlocks(content)), [content]);

    if (!content.trim()) {
        return <span className="text-gray-400 italic text-sm">Aucun contenu</span>;
    }

    return (
        <div className="space-y-4">
            {blocks.map((block, i) => {
                switch (block.type) {
                    case 'definition':
                        return <DefinitionBlock key={i} title={block.title} content={block.content} />;
                    case 'theorem':
                        return <TheoremBlock key={i} title={block.title} content={block.content} />;
                    case 'proof':
                        return <ProofBlock key={i} title={block.title} content={block.content} />;
                    case 'example':
                        return <ExampleBlock key={i} title={block.title} content={block.content} />;
                    case 'remark':
                        return <RemarkBlock key={i} title={block.title} content={block.content} />;
                    case 'note':
                        return <NoteBlock key={i} title={block.title} content={block.content} />;
                    case 'image_prompt':
                        return <ImagePromptBlock key={i} title={block.title} content={block.content} />;
                    case 'heading':
                        const isH3 = block._level === 3;
                        return (
                            <div key={i} className={`flex items-baseline gap-2 ${isH3 ? 'mt-3 ml-4' : 'mt-6'}`}>
                                <span className={`font-mono shrink-0 text-gray-400 dark:text-gray-500 select-none ${isH3 ? 'text-xs' : 'text-sm'}`}>
                                    {block._prefix}
                                </span>
                                <span className={`font-bold text-gray-900 dark:text-white leading-snug ${isH3 ? 'text-base' : 'text-lg'}`}>
                                    {block.content}
                                </span>
                            </div>
                        );
                    default:
                        // paragraph
                        return (
                            <div key={i} className="prose prose-sm dark:prose-invert max-w-none
                                [&_.katex-display]:overflow-x-auto [&_.katex-display]:py-1">
                                <ReactMarkdown {...MD}>{block.content}</ReactMarkdown>
                            </div>
                        );
                }
            })}
        </div>
    );
}
